<template>
  <PublicLayout>
    <div class="privacy-policy">
      <!-- Main Content -->
      <div class="content">
        <div class="policy-container">
          <h1>Privacy Policy</h1>
          <p class="last-updated">Last updated: {{ currentDate }}</p>

          <section class="policy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Prec8 ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you understand how we collect, use, and safeguard your information when you use our Slack application and related services.
            </p>
            <p>
              This Privacy Policy explains how we handle information when you use Prec8 to recognize and appreciate your team members through our Slack integration.
            </p>
          </section>

          <section class="policy-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Slack Profile Information:</strong> When you connect Prec8 to your Slack workspace, we collect your Slack user ID, display name, email address, and profile information.</li>
              <li><strong>Workspace Information:</strong> We collect your Slack workspace ID, workspace name, and workspace settings.</li>
              <li><strong>Appreciation Messages:</strong> When you send appreciation messages using our emoji system, we collect the message content, recipients, and associated emoji usage.</li>
            </ul>

            <h3>2.2 Information We Collect Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> We collect information about how you use Prec8, including which features you use and how often.</li>
              <li><strong>Technical Information:</strong> We collect technical information such as your IP address, browser type, and device information.</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain the Prec8 service</li>
              <li>Process and track appreciation tokens and rewards</li>
              <li>Generate leaderboards and statistics</li>
              <li>Send you notifications about your account and activity</li>
              <li>Improve our services and develop new features</li>
              <li>Ensure the security and integrity of our platform</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
            <ul>
              <li><strong>Within Your Workspace:</strong> Appreciation messages and token information are visible to members of your Slack workspace who have access to Prec8.</li>
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who help us operate our service (e.g., hosting providers, analytics services).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication measures</li>
              <li>Secure hosting infrastructure</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>6. Data Retention</h2>
            <p>We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Specifically:</p>
            <ul>
              <li><strong>Account Information:</strong> Retained while your account is active and for a reasonable period after deactivation.</li>
              <li><strong>Appreciation Data:</strong> Retained to maintain leaderboards and statistics.</li>
              <li><strong>Usage Data:</strong> Retained for analytics and service improvement purposes.</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> You can request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You can request that we correct inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> You can request deletion of your personal information, subject to certain exceptions.</li>
              <li><strong>Portability:</strong> You can request a copy of your data in a portable format.</li>
              <li><strong>Opt-out:</strong> You can opt out of certain communications and data processing activities.</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>8. Third-Party Services</h2>
            <p>Our service integrates with Slack and may use other third-party services. These services have their own privacy policies, and we encourage you to review them:</p>
            <ul>
              <li><strong>Slack:</strong> Our service operates within Slack's platform and is subject to Slack's privacy policy.</li>
              <li><strong>Firebase:</strong> We use Firebase for data storage and hosting services.</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2>9. Children's Privacy</h2>
            <p>Prec8 is not intended for use by individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>
          </section>

          <section class="policy-section">
            <h2>10. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.</p>
          </section>

          <section class="policy-section">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of Prec8 after any changes constitutes acceptance of the updated policy.</p>
          </section>

          <section class="policy-section">
            <h2>12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <div class="contact-info">
              <p><strong>Email:</strong> privacy@prec8.com</p>
              <p><strong>Website:</strong> <a href="https://prec8.com" class="link">https://prec8.com</a></p>
            </div>
          </section>

          <div class="policy-footer">
            <button @click="goHome" class="home-button">
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  </PublicLayout>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import PublicLayout from './components/PublicLayout.vue'

@Options({
  components: {
    PublicLayout
  },
})
export default class PrivacyPolicy extends Vue {
  get currentDate() {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  goHome() {
    this.$router.push('/')
  }
}
</script>

<style lang="scss" scoped>
.privacy-policy {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
  line-height: 1.6;
  min-height: 100vh;
  background: #f8fafc;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e1e5e9;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-logo {
  height: 40px;
  width: auto;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.header-nav {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.nav-link {
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background: #f6f9fc;
    color: #1a1a1a;
  }
}

.content {
  padding-top: 100px;
  padding-bottom: 4rem;
}

.policy-container {
  padding: 0 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e1e5e9;
  width: 100%;
}

h1 {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
  text-align: center;
  padding-top: 3rem;
}

.last-updated {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 3rem;
  font-style: italic;
}

.policy-section {
  margin-bottom: 2.5rem;
  padding: 0 2rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    border-bottom: 2px solid #f0f0f0;
    padding-bottom: 0.5rem;
  }

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem 0;
    color: #333;
  }

  p {
    margin-bottom: 1rem;
    color: #555;
    line-height: 1.7;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      color: #555;
      line-height: 1.6;

      strong {
        color: #1a1a1a;
        font-weight: 600;
      }
    }
  }
}

.contact-info {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #FF6B6B;
  margin-top: 1rem;

  p {
    margin-bottom: 0.5rem;
    color: #555;

    strong {
      color: #1a1a1a;
    }
  }
}

.link {
  color: #FF6B6B;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.policy-footer {
  text-align: center;
  padding: 3rem 2rem 2rem;
  border-top: 1px solid #e1e5e9;
  margin-top: 2rem;
}

.home-button {
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 107, 0.4);
  }
}

@media (max-width: 768px) {
  .content {
    padding-top: 80px;
  }

  .policy-container {
    margin: 0 1rem;
    padding: 0 1rem;
  }

  .policy-section {
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
    padding-top: 2rem;
  }

  .header-content {
    padding: 1rem;
  }

  .header-nav {
    gap: 0.5rem;
  }

  .nav-link {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
}
</style> 